import { LOGIN_STEPS, OTP_METHODS } from '@/data/Content/Login';
import { combineSX } from '@/utils/combineSX';
import { Box, SxProps, Typography } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { RegistrationOrigin } from '@/data/Content/Registration';

const getTitleMarginBottom = (
	step: LOGIN_STEPS,
	showInDialog: boolean,
	showPreviousMethodHint: boolean
) => {
	if (step === LOGIN_STEPS.SELECT_OTP_METHOD) {
		return showPreviousMethodHint ? '0px' : '15px';
	}

	if (showInDialog) {
		return '14px';
	}

	if (step === LOGIN_STEPS.INITIAL_LOGON_ID) {
		return '0px';
	}

	return '15px';
};

const getOtpTitleText = (
	otpMethod: OTP_METHODS | undefined,
	maskedPhone?: string | null,
	maskedEmail?: string | null
) => {
	if (otpMethod === OTP_METHODS.SMS) {
		return `mensaje al teléfono ${maskedPhone}`;
	}

	return `correo a ${maskedEmail}`;
};

const getDefaultTitleText = (origin?: RegistrationOrigin) => {
	if (['quick_cart', 'cart'].includes(origin ?? '')) {
		return 'INICIA SESIÓN';
	}

	return '¡Hola de nuevo!';
};

const getTitleText = ({
	step,
	otpMethod,
	maskedPhone,
	maskedEmail,
	origin,
}: Pick<Props, 'step' | 'otpMethod' | 'maskedPhone' | 'maskedEmail' | 'origin'>) => {
	if (step === LOGIN_STEPS.SELECT_OTP_METHOD) {
		return '¿Cómo quieres ingresar?';
	}

	if (step === LOGIN_STEPS.LOGIN_OTP) {
		return `Escribe el código de 6 dígitos que enviamos por ${getOtpTitleText(
			otpMethod,
			maskedPhone,
			maskedEmail
		)}`;
	}

	return getDefaultTitleText(origin);
};

const getInitialSubtitle = (origin?: RegistrationOrigin) =>
	['quick_cart', 'cart'].includes(origin ?? '')
		? 'Completa tus datos para ingresar y hacer tu compra'
		: 'Inicia sesión con tu cuenta para continuar';

const getTitleComponent = (showInDialog: boolean) => (showInDialog ? 'p' : 'h1');

const getSubtitleComponent = (showInDialog: boolean) => (showInDialog ? 'p' : 'h2');

const getMainTitleFontSize = (sx?: SxProps) =>
	(sx as any)?.['fontSize']
		? (sx as any)['fontSize']
		: { md: 'var(--font-size-xl)', xs: 'var(--font-size-lg)' };

const getInitialSubtitleFontSize = (showInDialog: boolean) =>
	showInDialog ? '15px' : 'var(--font-size-sm)';

const getPreviousMethodHintMarginBottom = (showInDialog: boolean) =>
	showInDialog ? '15px' : 'var(--font-size-sm)';

const getInitialSubtitleMarginBottom = (showInDialog: boolean) =>
	showInDialog ? '15px' : '20px';

type Props = {
	step: LOGIN_STEPS;
	otpMethod?: OTP_METHODS;
	maskedPhone?: string | null;
	maskedEmail?: string | null;
	showPreviousMethodHint?: boolean;
	showInDialog?: boolean;
	sx?: SxProps;
	origin?: RegistrationOrigin;
	onToggleBackStep: () => void;
};

export const SingleLoginLessTitle: React.FC<Props> = ({
	step,
	otpMethod,
	maskedPhone,
	maskedEmail,
	showPreviousMethodHint = false,
	showInDialog = false,
	origin = 'unknown',
	sx,
	onToggleBackStep,
}) => (
	<>
		{step !== LOGIN_STEPS.INITIAL_LOGON_ID ? (
			<Box sx={{ width: '100%', marginBottom: '20px' }}>
				<ArrowBackIosIcon
					sx={{ fontSize: '24px', cursor: 'pointer', fill: '#2C2C2C', alignSelf: 'flex-start' }}
					onClick={onToggleBackStep}
				/>
			</Box>
		) : null}
		<Typography
			component={getTitleComponent(showInDialog)}
			sx={combineSX([
				{
					fontSize: getMainTitleFontSize(sx),
					fontWeight: '800',
					letterSpacing: '0px',
					color: '#2C2C2C',
					textTransform: 'uppercase',
					opacity: 1,
					marginBottom: getTitleMarginBottom(step, showInDialog, showPreviousMethodHint),
				},
				sx,
			])}
		>
			{getTitleText({ step, otpMethod, maskedPhone, maskedEmail, origin })}
		</Typography>
		{step === LOGIN_STEPS.SELECT_OTP_METHOD && showPreviousMethodHint ? (
			<Typography
				component={getSubtitleComponent(showInDialog)}
				sx={{
					fontSize: '15px',
					fontWeight: 'normal',
					letterSpacing: '0px',
					color: '#2C2C2C',
					opacity: 0.85,
					mt: '0px !important',
					textTransform: 'none',
					marginBottom: getPreviousMethodHintMarginBottom(showInDialog),
				}}
			>
				{'Usamos tu método anterior. Puedes cambiarlo.'}
			</Typography>
		) : null}
		{step === LOGIN_STEPS.INITIAL_LOGON_ID ? (
			<Typography
				component={getSubtitleComponent(showInDialog)}
				sx={{
					fontSize: getInitialSubtitleFontSize(showInDialog),
					fontWeight: 'normal',
					letterSpacing: '0px',
					color: '#2C2C2C',
					opacity: 1,
					mt: '0px !important',
					textTransform: 'none',
					marginBottom: getInitialSubtitleMarginBottom(showInDialog),
				}}
			>
				{getInitialSubtitle(origin)}
			</Typography>
		) : null}
	</>
);
